import { createDateFormatter, displayDate, type TimezoneMode } from "./dateTime";

export function isFullDayDateRange(start: Date, end: Date): boolean {
  const midnight = (date: Date) =>
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0;
  return (
    end.getTime() > start.getTime() &&
    midnight(start) &&
    (midnight(end) ||
      (end.getUTCHours() === 23 &&
        end.getUTCMinutes() === 59 &&
        (end.getUTCSeconds() === 0 || end.getUTCSeconds() === 59)))
  );
}

export function formatCompactDateRange(
  start: Date,
  end: Date,
  timezone: "utc" | "local" = "utc",
): string {
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start)
    return "Choose dates";
  const dateOnly = isFullDayDateRange(displayDate(start, timezone), displayDate(end, timezone));
  const displayUtc = timezone === "utc";
  const last = dateOnly ? new Date(end.getTime() - 1) : end;
  const sameYear = displayUtc
    ? start.getUTCFullYear() === last.getUTCFullYear()
    : start.getFullYear() === last.getFullYear();
  const sameMonth =
    sameYear &&
    (displayUtc
      ? start.getUTCMonth() === last.getUTCMonth()
      : start.getMonth() === last.getMonth());
  const sameDay =
    sameMonth &&
    (displayUtc ? start.getUTCDate() === last.getUTCDate() : start.getDate() === last.getDate());
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
    ...(dateOnly ? {} : { hour: "2-digit", minute: "2-digit", hourCycle: "h23" as const }),
    timeZone: displayUtc ? "UTC" : undefined,
  };
  const formatter = createDateFormatter(timezone, options);
  let label = formatter.format(start);
  if (!dateOnly || !sameDay) {
    const startFormatter = createDateFormatter(timezone, {
      ...options,
      year: !dateOnly && sameDay ? "numeric" : sameYear ? undefined : "numeric",
      month: dateOnly && sameMonth ? undefined : "short",
    });
    const endFormatter =
      !dateOnly && sameDay
        ? createDateFormatter(timezone, {
            hour: "2-digit",
            minute: "2-digit",
            hourCycle: "h23",
            timeZone: options.timeZone,
          })
        : formatter;
    label = `${startFormatter.format(start)} \u2013 ${endFormatter.format(last)}`;
  }
  return label;
}

export function formatContextDateRange(start: Date, end: Date, timezone: TimezoneMode): string {
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start)
    return "Choose dates";
  const first = displayDate(start, timezone);
  const last = displayDate(new Date(end.getTime() - 1), timezone);
  return formatCompactDateRange(
    new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), first.getUTCDate())),
    new Date(Date.UTC(last.getUTCFullYear(), last.getUTCMonth(), last.getUTCDate() + 1)),
    "utc",
  );
}

export function formatDateRangeTooltip(start: Date, end: Date, timezone: TimezoneMode): string {
  const formatter = createDateFormatter(timezone, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  return `${formatter.format(start)} \u2013 ${formatter.format(end)} (${formatter.resolvedOptions().timeZone})`;
}

export function formatPeriodDateRange(
  from: string,
  to: string,
  timezone: TimezoneMode,
  compact = true,
): string | undefined {
  const startMs = Date.parse(from);
  const endMs = Date.parse(to);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return undefined;
  const start = displayDate(new Date(startMs), timezone);
  const end = displayDate(new Date(endMs - 1), timezone);
  const month = createDateFormatter("utc", { month: "short" }, "en-US");
  const fullDate = createDateFormatter("utc", { day: "numeric", month: "long", year: "numeric" });
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  const sameMonth = sameYear && start.getUTCMonth() === end.getUTCMonth();
  const sameDay = sameMonth && start.getUTCDate() === end.getUTCDate();
  const shortDate = (date: Date) => `${date.getUTCDate()} ${month.format(date)}`;
  if (compact) {
    if (sameMonth)
      return sameDay ? shortDate(start) : `${start.getUTCDate()}\u2013${shortDate(end)}`;
    return `${shortDate(start)}\u2013${shortDate(end)}`;
  }
  if (!sameYear) return `${fullDate.format(start)}\u2013${fullDate.format(end)}`;
  if (!sameMonth)
    return `${start.getUTCDate()} ${createDateFormatter("utc", { month: "long" }).format(start)}\u2013${fullDate.format(end)}`;
  return sameDay ? fullDate.format(start) : `${start.getUTCDate()}\u2013${fullDate.format(end)}`;
}
